import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Search, Eye, X, RefreshCw, Phone, Building2, Mail, MapPin,
  Package, Calendar, CreditCard, Edit, Save, ChevronRight, Bell,
  ArrowLeft, User, Truck, Plus
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { API, formatPrice, formatDate, formatActivityType, getActivityTypeBadgeClass, STATUS_BADGES } from '../utils';
import { PackName } from '../../ui/PackName';

const getStatusBadge = (status) => {
  const badge = STATUS_BADGES[status] || STATUS_BADGES.pending;
  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>{badge.text}</span>;
};

const CustomersView = () => {
    const [clients, setClients] = useState([]);
const [searchTerm, setSearchTerm] = useState('');
    const [selectedClient, setSelectedClient] = useState(null);
    const [clientDetails, setClientDetails] = useState(null);
    const [loadingClient, setLoadingClient] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editedProfile, setEditedProfile] = useState({});
    const [savingProfile, setSavingProfile] = useState(false);
    const [sendingReminder, setSendingReminder] = useState(false);

    // Create-contact modal state
    const [showNewContactModal, setShowNewContactModal] = useState(false);
    const [savingContact, setSavingContact] = useState(false);
    const [newContact, setNewContact] = useState({
      firstName: '', lastName: '', email: '', phone: '', company: '',
      siren: '', activityType: '',
      address: '', postalCode: '', city: '',
      customer_type: 'contact', notes: '',
    });

    const reloadClients = async () => {
      try {
        const res = await axios.get(`${API}/admin/clients`, { withCredentials: true });
        setClients(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('reloadClients', e);
      }
    };

    const submitNewContact = async () => {
      if (!newContact.email || !newContact.email.includes('@')) {
        toast.error('Email valide requis');
        return;
      }
      setSavingContact(true);
      try {
        await axios.post(`${API}/admin/clients`, newContact, { withCredentials: true });
        toast.success(newContact.customer_type === 'client' ? 'Client créé' : 'Contact créé');
        setShowNewContactModal(false);
        setNewContact({
          firstName: '', lastName: '', email: '', phone: '', company: '',
          siren: '', activityType: '',
          address: '', postalCode: '', city: '',
          customer_type: 'contact', notes: '',
        });
        await reloadClients();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Erreur lors de la création');
      } finally {
        setSavingContact(false);
      }
    };

    const setCustomerType = async (email, newType) => {
      try {
        await axios.patch(
          `${API}/admin/clients/${encodeURIComponent(email)}/customer-type`,
          { customer_type: newType },
          { withCredentials: true }
        );
        toast.success(newType === 'client' ? 'Statut changé : Client' : 'Statut changé : Contact');
        await reloadClients();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Erreur de mise à jour du statut');
      }
    };

    // Fetch clients from unified endpoint (reservations + manual_orders + quotes merged)
    useEffect(() => {
      const fetchClients = async () => {
        try {
          const res = await axios.get(`${API}/admin/clients`, { withCredentials: true });
          setClients(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
          console.error('Error fetching clients:', e);
        }
      };
      fetchClients();
    }, []);
    
    const filteredClients = clients.filter(client => {
      const search = searchTerm.toLowerCase();
      return (
        client.firstName?.toLowerCase().includes(search) ||
        client.lastName?.toLowerCase().includes(search) ||
        client.email?.toLowerCase().includes(search) ||
        client.company?.toLowerCase().includes(search)
      );
    });

    const openClientDetails = async (client) => {
      setSelectedClient(client);
      setLoadingClient(true);
      try {
        const response = await axios.get(`${API}/admin/clients/${encodeURIComponent(client.email)}/details`, {
          withCredentials: true
        });
        setClientDetails(response.data);
        setEditedProfile(response.data.profile || {});
      } catch (error) {
        console.error('Error fetching client details:', error);
        toast.error('Erreur lors du chargement des détails client');
      } finally {
        setLoadingClient(false);
      }
    };

    const saveClientProfile = async () => {
      setSavingProfile(true);
      try {
        await axios.put(`${API}/admin/clients/${encodeURIComponent(selectedClient.email)}/update`, editedProfile, {
          withCredentials: true
        });
        toast.success('Client mis à jour avec succès');
        setEditMode(false);
        // Refresh client details
        openClientDetails(selectedClient);
        // Refresh clients list
        fetchData();
      } catch (error) {
        console.error('Error saving client:', error);
        toast.error('Erreur lors de la sauvegarde');
      } finally {
        setSavingProfile(false);
      }
    };

    const sendRenewalReminder = async () => {
      setSendingReminder(true);
      try {
        await axios.post(`${API}/admin/clients/${encodeURIComponent(selectedClient.email)}/send-renewal-reminder`, {}, {
          withCredentials: true
        });
        toast.success('Rappel de renouvellement envoyé !');
      } catch (error) {
        console.error('Error sending reminder:', error);
        toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
      } finally {
        setSendingReminder(false);
      }
    };

    const closeClientModal = () => {
      setSelectedClient(null);
      setClientDetails(null);
      setEditMode(false);
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-xl font-bold text-white">Clients ({clients.length})</h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Rechercher un client..." 
                className="pl-10 bg-slate-800 border-slate-700 text-white w-64"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="client-search-input"
              />
            </div>
            <Button
              onClick={() => setShowNewContactModal(true)}
              className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold"
              data-testid="new-contact-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Nouveau contact
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
            <p className="text-sm text-slate-400">Total clients</p>
            <p className="text-2xl font-bold text-white">{clients.length}</p>
          </div>
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
            <p className="text-sm text-slate-400">Clients actifs</p>
            <p className="text-2xl font-bold text-green-400">{clients.filter(c => c.active_subscriptions > 0).length}</p>
          </div>
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
            <p className="text-sm text-slate-400">Total réservations</p>
            <p className="text-2xl font-bold text-[#DFFF00]">{clients.reduce((acc, c) => acc + c.total_reservations, 0)}</p>
          </div>
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
            <p className="text-sm text-slate-400">CA total</p>
            <p className="text-2xl font-bold text-white">{formatPrice(clients.reduce((acc, c) => acc + c.total_spent, 0))}</p>
          </div>
        </div>

        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Client</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Entreprise</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Activité</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Ville</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Réservations / Devis</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">CA Total</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Statut</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filteredClients.map((client) => (
                <tr key={client.email} className="hover:bg-slate-800/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#DFFF00] flex items-center justify-center">
                        <span className="text-black font-medium text-sm">
                          {client.firstName?.[0]}{client.lastName?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-white font-medium">{client.firstName} {client.lastName}</p>
                        <p className="text-xs text-slate-500">{client.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-300">{client.company || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${getActivityTypeBadgeClass(client.activityType)}`}>
                      {formatActivityType(client.activityType)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-400">{client.phone || '-'}</td>
                  <td className="px-4 py-3 text-sm text-slate-400">{client.city || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-xs">
                      {client.total_reservations > 0 && (
                        <span title="Réservations en ligne" className="px-1.5 py-0.5 rounded bg-[#DFFF00]/15 text-[#DFFF00]">
                          R: {client.total_reservations}
                        </span>
                      )}
                      {client.total_manual_orders > 0 && (
                        <span title="Commandes manuelles" className="px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-400">
                          CM: {client.total_manual_orders}
                        </span>
                      )}
                      {client.total_quotes > 0 && (
                        <span title="Devis" className="px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400">
                          D: {client.total_quotes}
                        </span>
                      )}
                      {!client.total_reservations && !client.total_manual_orders && !client.total_quotes && (
                        <span className="text-slate-500">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#DFFF00] font-medium">{formatPrice(client.total_spent)}</td>
                  <td className="px-4 py-3">
                    <select
                      value={client.customer_type}
                      onChange={(e) => setCustomerType(client.email, e.target.value)}
                      data-testid={`client-status-select-${client.email}`}
                      className={`text-xs font-semibold rounded-md px-2.5 py-1.5 border cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#DFFF00]/50 transition-colors ${
                        client.customer_type === 'client'
                          ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/25'
                          : 'bg-slate-500/15 text-slate-300 border-slate-500/40 hover:bg-slate-500/25'
                      }`}
                    >
                      <option value="contact" className="bg-slate-900 text-slate-300">Contact</option>
                      <option value="client" className="bg-slate-900 text-emerald-400">Client</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openClientDetails(client)}
                      className="text-[#DFFF00] hover:text-[#c8e600] hover:bg-[#DFFF00]/10"
                      data-testid={`client-row-view-${client.email}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Fiche
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        {/* Client Details Modal */}
        {selectedClient && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#DFFF00]/20 flex items-center justify-center">
                    <User className="w-6 h-6 text-[#DFFF00]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">
                      {selectedClient.firstName} {selectedClient.lastName}
                    </h2>
                    <p className="text-sm text-slate-400">{selectedClient.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!editMode && clientDetails?.stats?.active_rentals > 0 && (
                    <Button
                      onClick={sendRenewalReminder}
                      disabled={sendingReminder}
                      size="sm"
                      className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30"
                      title="Les rappels sont envoyés automatiquement 5 jours avant chaque renouvellement"
                    >
                      {sendingReminder ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Mail className="w-4 h-4 mr-2" />
                      )}
                      Rappel manuel
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={closeClientModal}
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-4 overflow-y-auto flex-1">
                {loadingClient ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-[#DFFF00] animate-spin" />
                  </div>
                ) : clientDetails ? (
                  <div className="grid lg:grid-cols-3 gap-6">
                    {/* Left Column - Client Info */}
                    <div className="lg:col-span-1 space-y-4">
                      {/* Stats */}
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-slate-400 mb-3">Statistiques</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-slate-900 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-[#DFFF00]">{clientDetails.stats.total_reservations}</p>
                            <p className="text-xs text-slate-500">Locations</p>
                          </div>
                          <div className="bg-slate-900 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-green-400">{clientDetails.stats.active_rentals}</p>
                            <p className="text-xs text-slate-500">Actives</p>
                          </div>
                          <div className="col-span-2 bg-slate-900 rounded-lg p-3 text-center">
                            <p className="text-2xl font-bold text-white">{formatPrice(clientDetails.stats.total_spent)}</p>
                            <p className="text-xs text-slate-500">CA Total</p>
                          </div>
                        </div>
                      </div>

                      {/* Profile Info / Edit */}
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-medium text-slate-400">Informations</h4>
                          {!editMode ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditMode(true)}
                              className="text-[#DFFF00] hover:text-[#c8e600]"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Modifier
                            </Button>
                          ) : (
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditMode(false)}
                                className="text-slate-400"
                              >
                                Annuler
                              </Button>
                              <Button
                                size="sm"
                                onClick={saveClientProfile}
                                disabled={savingProfile}
                                className="bg-[#DFFF00] hover:bg-[#c8e600] text-black"
                              >
                                {savingProfile ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Sauver'}
                              </Button>
                            </div>
                          )}
                        </div>

                        {editMode ? (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="text"
                                value={editedProfile.first_name || ''}
                                onChange={(e) => setEditedProfile({...editedProfile, first_name: e.target.value})}
                                placeholder="Prénom"
                                className="bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm"
                              />
                              <input
                                type="text"
                                value={editedProfile.last_name || ''}
                                onChange={(e) => setEditedProfile({...editedProfile, last_name: e.target.value})}
                                placeholder="Nom"
                                className="bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm"
                              />
                            </div>
                            <input
                              type="text"
                              value={editedProfile.phone || ''}
                              onChange={(e) => setEditedProfile({...editedProfile, phone: e.target.value})}
                              placeholder="Téléphone"
                              className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm"
                            />
                            <input
                              type="text"
                              value={editedProfile.company || ''}
                              onChange={(e) => setEditedProfile({...editedProfile, company: e.target.value})}
                              placeholder="Société"
                              className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm"
                            />
                            <select
                              value={editedProfile.activity_type || ''}
                              onChange={(e) => setEditedProfile({...editedProfile, activity_type: e.target.value})}
                              className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm"
                            >
                              <option value="">Type d'activité</option>
                              <option value="prestataire_evenementiel">Prestataire événementiel</option>
                              <option value="dj">DJ</option>
                              <option value="etablissement">Établissement</option>
                              <option value="particulier">Particulier</option>
                              <option value="autre">Autre</option>
                            </select>
                            <div className="border-t border-slate-700 pt-3 mt-3">
                              <p className="text-xs text-slate-500 mb-2">Adresse de livraison</p>
                              <input
                                type="text"
                                value={editedProfile.delivery_address || ''}
                                onChange={(e) => setEditedProfile({...editedProfile, delivery_address: e.target.value})}
                                placeholder="Adresse"
                                className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm mb-2"
                              />
                              <div className="grid grid-cols-2 gap-2">
                                <input
                                  type="text"
                                  value={editedProfile.delivery_city || ''}
                                  onChange={(e) => setEditedProfile({...editedProfile, delivery_city: e.target.value})}
                                  placeholder="Ville"
                                  className="bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm"
                                />
                                <input
                                  type="text"
                                  value={editedProfile.delivery_postal_code || ''}
                                  onChange={(e) => setEditedProfile({...editedProfile, delivery_postal_code: e.target.value})}
                                  placeholder="CP"
                                  className="bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-500">Nom</span>
                              <span className="text-white">{clientDetails.profile.first_name} {clientDetails.profile.last_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Téléphone</span>
                              <span className="text-white">{clientDetails.profile.phone || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Société</span>
                              <span className="text-white">{clientDetails.profile.company || '-'}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-slate-500">Activité</span>
                              <span className={`px-2 py-0.5 text-xs rounded-full border ${getActivityTypeBadgeClass(clientDetails.profile.activity_type)}`}>
                                {formatActivityType(clientDetails.profile.activity_type)}
                              </span>
                            </div>
                            <div className="border-t border-slate-700 pt-2 mt-2">
                              <p className="text-slate-500 mb-1">Adresse livraison</p>
                              <p className="text-white text-xs">
                                {clientDetails.profile.delivery_address || '-'}<br/>
                                {clientDetails.profile.delivery_postal_code} {clientDetails.profile.delivery_city}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Column - Rental History */}
                    <div className="lg:col-span-2">
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-slate-400 mb-4">Historique des locations</h4>
                        {clientDetails.reservations.length === 0 ? (
                          <p className="text-slate-500 text-center py-8">Aucune location</p>
                        ) : (
                          <div className="space-y-3 max-h-[400px] overflow-y-auto">
                            {clientDetails.reservations.map((res) => (
                              <div key={res.id} className="bg-slate-900 rounded-lg p-4 border border-slate-800">
                                <div className="flex items-start justify-between mb-2">
                                  <div>
                                    <p className="font-medium text-white"><PackName name={res.pack_name} /></p>
                                    <p className="text-xs text-slate-500">{res.pack_surface}</p>
                                  </div>
                                  {getStatusBadge(res.status)}
                                </div>
                                <div className="grid grid-cols-3 gap-4 text-xs mt-3">
                                  <div>
                                    <p className="text-slate-500">Date début</p>
                                    <p className="text-white">{res.rental_start_date ? formatDate(res.rental_start_date) : formatDate(res.created_at)}</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500">Mensualité</p>
                                    <p className="text-[#DFFF00] font-medium">{formatPrice(res.monthly_price)}/mois</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500">Caution</p>
                                    <p className="text-white">{formatPrice(res.caution)}</p>
                                  </div>
                                </div>
                                {res.status === 'shipped' && res.shipping?.tracking_number && (
                                  <div className="mt-3 pt-3 border-t border-slate-800 flex items-center gap-2 text-xs">
                                    <Truck className="w-3 h-3 text-green-400" />
                                    <span className="text-slate-400">N° suivi :</span>
                                    <span className="text-white font-mono">{res.shipping.tracking_number}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-slate-800">
                <Button
                  variant="outline"
                  onClick={closeClientModal}
                  className="border-slate-700 text-slate-300"
                >
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* New contact / client modal */}
        {showNewContactModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="new-contact-modal">
            <div className="bg-slate-900 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-slate-800">
              <div className="flex items-center justify-between p-6 border-b border-slate-800 sticky top-0 bg-slate-900 z-10">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Plus className="w-5 h-5 text-[#DFFF00]" /> Nouveau contact / client
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setShowNewContactModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="p-6 space-y-5">
                {/* Type selector */}
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">Statut</label>
                  <div className="flex gap-2">
                    {[
                      { id: 'contact', label: 'Contact (prospect)', desc: "N'a pas encore loué" },
                      { id: 'client', label: 'Client', desc: 'A déjà une location ou paiement' },
                    ].map(opt => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setNewContact(p => ({ ...p, customer_type: opt.id }))}
                        data-testid={`new-contact-type-${opt.id}`}
                        className={`flex-1 px-4 py-3 rounded-lg border-2 text-left transition-all ${
                          newContact.customer_type === opt.id
                            ? 'border-[#DFFF00] bg-[#DFFF00]/10 text-white'
                            : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-500'
                        }`}
                      >
                        <div className="font-medium text-sm">{opt.label}</div>
                        <div className="text-xs opacity-70 mt-0.5">{opt.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Identity */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Prénom</label>
                    <Input value={newContact.firstName} onChange={e => setNewContact(p => ({ ...p, firstName: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" data-testid="nc-firstName" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Nom</label>
                    <Input value={newContact.lastName} onChange={e => setNewContact(p => ({ ...p, lastName: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" data-testid="nc-lastName" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Email *</label>
                    <Input type="email" value={newContact.email} onChange={e => setNewContact(p => ({ ...p, email: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" required data-testid="nc-email" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Téléphone</label>
                    <Input value={newContact.phone} onChange={e => setNewContact(p => ({ ...p, phone: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" data-testid="nc-phone" />
                  </div>
                </div>

                {/* Company */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Entreprise</label>
                    <Input value={newContact.company} onChange={e => setNewContact(p => ({ ...p, company: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" data-testid="nc-company" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">SIREN</label>
                    <Input value={newContact.siren} onChange={e => setNewContact(p => ({ ...p, siren: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" data-testid="nc-siren" />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Type d'activité</label>
                  <select
                    value={newContact.activityType}
                    onChange={e => setNewContact(p => ({ ...p, activityType: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2 text-sm"
                    data-testid="nc-activityType"
                  >
                    <option value="">Sélectionner...</option>
                    <option value="bar_restaurant">Bar / Restaurant</option>
                    <option value="club_discotheque">Club / Discothèque</option>
                    <option value="evenementiel">Événementiel</option>
                    <option value="hotel">Hôtel</option>
                    <option value="mariage_particulier">Mariage / Particulier</option>
                    <option value="entreprise">Entreprise</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                {/* Address */}
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Adresse</label>
                  <Input value={newContact.address} onChange={e => setNewContact(p => ({ ...p, address: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" data-testid="nc-address" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Code postal</label>
                    <Input value={newContact.postalCode} onChange={e => setNewContact(p => ({ ...p, postalCode: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" data-testid="nc-postalCode" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Ville</label>
                    <Input value={newContact.city} onChange={e => setNewContact(p => ({ ...p, city: e.target.value }))} className="bg-slate-800 border-slate-700 text-white" data-testid="nc-city" />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Notes internes</label>
                  <textarea
                    value={newContact.notes}
                    onChange={e => setNewContact(p => ({ ...p, notes: e.target.value }))}
                    rows={3}
                    className="w-full bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2 text-sm resize-none"
                    placeholder="Source, contexte, points à retenir..."
                    data-testid="nc-notes"
                  />
                </div>
              </div>

              <div className="p-6 border-t border-slate-800 flex justify-end gap-2 sticky bottom-0 bg-slate-900">
                <Button variant="outline" onClick={() => setShowNewContactModal(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="nc-cancel">
                  Annuler
                </Button>
                <Button onClick={submitNewContact} disabled={savingContact || !newContact.email} className="bg-[#DFFF00] hover:bg-[#c8e600] text-black font-semibold" data-testid="nc-submit">
                  {savingContact ? 'Enregistrement...' : 'Créer'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

export default CustomersView;
